import type { ReportFilterKey } from '@/gql/graphql'
import type { FilterFieldConfig, FilterOption } from '@/lib/filters/types'

// ── report filter manifest (EW-T10, FR-8.5) ────────────────────────────────
//
// The renderer is driven by THIS map, not by a chain of `if (has('AS_OF_DATE'))` branches.
// Adding a filter key means adding a row here; it does not mean growing a conditional in the
// component. That is the whole point of FR-8.5 — the previous renderer had one hand-written
// branch per key, so every new filter was a code change in a growing if-chain.
//
// Keys are the real SCREAMING_SNAKE_CASE `ReportFilterKey` enum values (verified against the
// merged schema), not the handover doc's original camelCase.

export type FilterControlKind = 'date' | 'place' | 'enum'

export interface FilterFieldDef {
  /** User-facing label — also what a removable chip is prefixed with. */
  label: string
  kind: FilterControlKind
  /** Closed option set, for `kind: 'enum'`. */
  options?: readonly string[]
}

// Confirmed against BE source (packages/shared-types/src/entity-enums.ts CompanyStatus),
// matching src/lib/status_colors.ts key-for-key — kept in sync manually and enforced by the
// T0083 enum-coverage guard in report-filters.test.ts, not by the type system: the entityStatus
// filter value is an untyped JSON scalar server-side, so this mapping is inferred from BE
// behaviour, not type-checked.
export const ENTITY_STATUS_OPTIONS = [
  'ACTIVE', 'DORMANT', 'DISSOLVED', 'IN_LIQUIDATION', 'STRUCK_OFF', 'SUSPENDED', 'CONVERTED', 'CLOSED',
  'NOT_CONFIRMED',
] as const

export const FILTER_MANIFEST: Record<string, FilterFieldDef> = {
  AS_OF_DATE: { label: 'As of date', kind: 'date' },
  JURISDICTION: { label: 'Jurisdiction', kind: 'place' },
  ENTITY_STATUS: { label: 'Entity status', kind: 'enum', options: ENTITY_STATUS_OPTIONS },
  DATE_FROM: { label: 'Date from', kind: 'date' },
  DATE_TO: { label: 'Date to', kind: 'date' },
}

/**
 * DATE_FROM/DATE_TO are rendered as ONE range control (FR-8.4), so they are excluded from the
 * generic field loop and handled by the range control instead.
 */
export const DATE_RANGE_KEYS = ['DATE_FROM', 'DATE_TO'] as const

export function supportsDateRange(supportedFilters: ReportFilterKey[]): boolean {
  return supportedFilters.includes('DATE_FROM') || supportedFilters.includes('DATE_TO')
}

/** Supported keys that the generic loop renders — i.e. everything except the range pair. */
export function standaloneFilterKeys(supportedFilters: ReportFilterKey[]): string[] {
  return supportedFilters.filter(
    (k) => !(DATE_RANGE_KEYS as readonly string[]).includes(k) && FILTER_MANIFEST[k],
  )
}

/**
 * Maps manifest-driven keys (everything except the DATE_FROM/DATE_TO range pair — see
 * `standaloneFilterKeys`) onto `FilterFieldConfig`s for `FilterBar`/`FilterField` (EW-T06). The
 * `kind -> FilterType` mapping is the generic-renderer half of FR-8.5: a new filter key still
 * only means a new `FILTER_MANIFEST` row, never a new conditional here.
 */
export function fieldsFromManifest(keys: string[], placeOptions: FilterOption[] = []): FilterFieldConfig[] {
  return keys.map((key) => {
    const def = FILTER_MANIFEST[key]
    switch (def.kind) {
      case 'date':
        return { id: key, label: def.label, type: 'date' }
      case 'place':
        return {
          id: key, label: def.label, type: 'select',
          allLabel: `All ${def.label.toLowerCase()}s`,
          options: placeOptions,
        }
      case 'enum':
        return {
          id: key, label: def.label, type: 'select',
          allLabel: `All ${def.label.toLowerCase()}es`,
          options: (def.options ?? []).map((o) => ({ value: o, label: o })),
        }
    }
  })
}

export interface AppliedFilter {
  key: string
  label: string
  /** Display value — resolved via `valueLabels` where one exists (e.g. a place id -> name). */
  value: string
}

/**
 * The filters actually set, for the chip row and the count (FR-8.2). Empty/whitespace values
 * are not "applied" — an unset field must never present itself as an active filter.
 */
export function appliedFilters(
  supportedFilters: ReportFilterKey[],
  values: Record<string, string>,
  valueLabels: Record<string, string> = {},
): AppliedFilter[] {
  return supportedFilters
    .filter((k) => FILTER_MANIFEST[k])
    .filter((k) => (values[k] ?? '').trim() !== '')
    .map((k) => ({
      key: k,
      label: FILTER_MANIFEST[k].label,
      value: valueLabels[values[k]] ?? values[k],
    }))
}

/**
 * FR-8.4: `to >= from`, enforced in the UI before submission. Server validation remains
 * authoritative — this exists so a user is told immediately rather than after a round trip.
 * A partial range (only one end set) is NOT invalid; it is simply an open-ended range.
 */
export function isDateRangeInvalid(values: Record<string, string>): boolean {
  const from = (values.DATE_FROM ?? '').trim()
  const to = (values.DATE_TO ?? '').trim()
  if (!from || !to) return false
  const fromMs = Date.parse(from)
  const toMs = Date.parse(to)
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return false
  return toMs < fromMs
}

export const DATE_RANGE_INVALID_MESSAGE = 'The end date must be on or after the start date.'
